import { CmsPageSection, PageTemplate, Suburb } from '../../../models/content';

/**
 * The block preview's request (#263), as conductor-server's POST /website/block-preview takes it
 * (block-preview.request.ts there): blocks in their STORED shape, the sections editor's toMap() values, never
 * projected. The server projects them exactly as the External API does for the site's sync. Pure; tested in
 * block-preview-request.spec.ts.
 */

/** A page's block: the Pages editor, the Region editor. */
export interface PageBlockRequest {
  kind: 'page';
  /** The address the block sits on: data blocks scope from it. */
  url: string;
  sections: CmsPageSection[];
}

/** A suburb, place or sub-region page (#262): the template editor, the Page tab and its inserted-block editor. */
export interface SuburbPageRequest {
  kind: 'suburb';
  /** The record (content.suburbs.<id>). */
  recordId: string;
  /** The Page tab's unsaved record, as a save would write it; null = as published. */
  fields: Suburb | null;
  /** A draft of the Suburb or Sub-region template, as a save would write it; null = as published. */
  template: PageTemplate | null;
  /** One block to draw on its own; null = the whole page. */
  focus: string | null;
}

export type BlockPreviewRequest = PageBlockRequest | SuburbPageRequest;

/** The block being edited, on its own, at the page's address; null while no block is active. */
export function pageBlockRequest(
  address: string | null | undefined,
  sections: Record<string, CmsPageSection>,
  activeId: string | null
): PageBlockRequest | null {
  const active = activeId ? sections[activeId] : undefined;
  if (!active) return null;
  return { kind: 'page', url: pageAddress(address), sections: [active] };
}

/** A suburb page, or one of its blocks (`focus`); null until a record is picked. */
export function suburbPageRequest(
  recordId: string | null | undefined,
  fields: Suburb | null,
  template: PageTemplate | null,
  focus: string | null
): SuburbPageRequest | null {
  if (!recordId) return null;
  return { kind: 'suburb', recordId, fields, template, focus };
}

/**
 * The address as the site reads it: a leading slash, no trailing one. A page with no address yet (or one the address
 * check would refuse, with spaces in it) previews at "/", as the spec says.
 */
export function pageAddress(address: string | null | undefined): string {
  const trimmed = (address ?? '').trim();
  if (!trimmed || /\s/.test(trimmed)) return '/';
  const path = `/${trimmed.replace(/^\/+/, '')}`.replace(/\/+$/, '');
  return path || '/';
}

/** Two requests are the same when they serialise the same: what the controller compares before sending. */
export function requestKey(request: BlockPreviewRequest | null): string {
  return JSON.stringify(request);
}
